import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, MessageCircleHeart, Sparkles, CalendarCheck, Handshake, ArrowRight, Star, MapPin, Landmark,
} from "lucide-react";
import api from "@/lib/api";
import { setJsonLd, setCanonical } from "@/lib/seo";
import { useLang } from "@/context/LanguageContext";
import PublicLayout from "@/components/layout/PublicLayout";
import PropertyCard from "@/components/PropertyCard";
import { SearchWidget } from "@/components/SearchWidget";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1726136056898-717ca659a1ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxpdGFsaWFuJTIwbGFrZSUyMHZpZXclMjB2YWNhdGlvbiUyMGhvdXNlJTIwYW1hbGZpfGVufDB8fHx8MTc4OTEzNjE0Nnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useLang();
  const [properties, setProperties] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [attractions, setAttractions] = useState([]);

  useEffect(() => {
    api.get("/public/properties").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/public/destinations").then((r) => setDestinations(r.data)).catch(() => {});
    api.get("/public/attractions").then((r) => setAttractions(r.data)).catch(() => {});
    setCanonical(window.location.origin + "/");
    setJsonLd("ld-org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Quattro Accommodations",
      "url": window.location.origin,
      "logo": "https://customer-assets-rejwkqb3.emergentagent.net/job_guest-retreat-finder/artifacts/ymwgc3ta_Logo%20Quattro%20Accommodations.png",
      "description": "Direct-booking portal for vacation rentals and B&Bs in Italy. Book directly with the owner, no middlemen.",
    });
  }, []);

  const BENEFITS = [
    { icon: ShieldCheck, title: t("home.b1t"), desc: t("home.b1d") },
    { icon: MessageCircleHeart, title: t("home.b2t"), desc: t("home.b2d") },
    { icon: Handshake, title: t("home.b3t"), desc: t("home.b3d") },
    { icon: Sparkles, title: t("home.b4t"), desc: t("home.b4d") },
    { icon: CalendarCheck, title: t("home.b5t"), desc: t("home.b5d") },
  ];

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Costa italiana" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-violet/90 via-brand-plum/55 to-brand-violet/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-28 md:pt-32 md:pb-36">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-4 animate-fade-up">{t("home.heroKicker")}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white leading-[1.08] max-w-3xl animate-fade-up" style={{ animationDelay: "0.1s" }}>{t("home.heroTitle")}</h1>
          <p className="mt-5 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>{t("home.heroSubtitle")}</p>
          <div className="mt-10 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <SearchWidget />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="vantaggi" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal mb-3">{t("home.benefitsKicker")}</p>
          <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum">{t("home.benefitsTitle")}</h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <div key={i} className="rounded-2xl bg-white p-7 border border-brand-plum/10 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300" data-testid={`benefit-card-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-brand-tealLight flex items-center justify-center mb-4">
                <b.icon className="text-brand-tealDark" size={22} />
              </div>
              <h3 className="font-serif text-xl text-brand-plum mb-2">{b.title}</h3>
              <p className="text-sm text-brand-plum/60 leading-relaxed">{b.desc}</p>
            </div>
          ))}
          <div className="rounded-2xl bg-brand-plum p-7 flex flex-col justify-between text-white">
            <div>
              <h3 className="font-serif text-2xl mb-2">{t("home.ownerCtaTitle")}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{t("home.ownerCtaDesc")}</p>
            </div>
            <Link to="/proprietari" className="mt-6">
              <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold w-full">
                {t("home.ownerCtaBtn")} <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* PROPERTIES */}
      <section className="bg-brand-warm py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal mb-3">{t("home.networkKicker")}</p>
              <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum">{t("home.networkTitle")}</h2>
              <p className="mt-3 text-brand-plum/60">{t("home.networkDesc")}</p>
            </div>
            <Link to="/case-vacanze">
              <Button variant="outline" className="rounded-full border-brand-plum/20 text-brand-plum hover:bg-brand-plum hover:text-white">
                {t("common.viewAll")} <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {properties.slice(0, 6).map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal mb-3">{t("home.destKicker")}</p>
          <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum">{t("home.destTitle")}</h2>
          <p className="mt-3 text-brand-plum/60">{t("home.destDesc")}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="destinations-grid">
          {destinations.map((d) => (
            <Link key={d.slug} to={`/case-vacanze/${d.slug}`} data-testid={`destination-card-${d.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[16/10] shadow-sm hover:shadow-xl transition-all duration-300">
              <img src={d.image} alt={`Case vacanze e B&B a ${d.city}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-violet/85 via-brand-violet/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                <div>
                  <h3 className="font-serif text-2xl text-white flex items-center gap-1.5"><MapPin size={18} className="text-brand-teal" /> {d.city}</h3>
                  <p className="text-sm text-white/70">{d.region}</p>
                </div>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/90 text-brand-plum">{d.count} {d.count === 1 ? t("home.house") : t("home.houses")}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ATTRACTIONS */}
      {attractions.length > 0 && (
        <section className="bg-brand-warm py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-10">
              <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal mb-3">{t("home.attrKicker")}</p>
              <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum">{t("home.attrTitle")}</h2>
              <p className="mt-3 text-brand-plum/60">{t("home.attrDesc")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="attractions-grid">
              {attractions.map((a) => (
                <Link key={a.slug} to={`/case-vacanze-vicino-a/${a.slug}`} data-testid={`attraction-card-${a.slug}`} className="group rounded-2xl bg-white border border-brand-plum/10 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={a.image} alt={a.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 text-xs font-medium px-2.5 py-1 rounded-full bg-white/90 text-brand-plum flex items-center gap-1"><Landmark size={12} className="text-brand-teal" /> {a.city}</span>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-serif text-xl text-brand-plum">{a.name}</h3>
                    <p className="text-sm text-brand-plum/60 mt-1.5 line-clamp-2 flex-1">{a.intro}</p>
                    <span className="mt-3 text-sm font-medium text-brand-teal flex items-center gap-1">{t("home.attrCard")} <ArrowRight size={14} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-3xl bg-brand-violet overflow-hidden relative p-10 md:p-16 text-center">
          <div className="relative">
            <Star className="mx-auto text-brand-teal mb-4" size={28} />
            <h2 className="text-3xl sm:text-4xl font-serif text-white max-w-2xl mx-auto">{t("home.ctaTitle")}</h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">{t("home.ctaDesc")}</p>
            <Link to="/case-vacanze" className="inline-block mt-8">
              <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold px-8 h-12">{t("home.ctaBtn")}</Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
